import { router, useGlobalSearchParams } from 'expo-router';
import { useCallback, useMemo, useState } from 'react';

import { useCreateOtpv2Mutation } from '@fhs-legacy/frontend/src/generated/graphql-gateway';
import { useAmplitudeContext } from '@fhs-legacy/frontend/src/state/amplitude';

import { AuthIdentifiers } from '../types';

import { useCRMEventsContext, useToast } from '.';

export type CreateOtpInput = {
  loginIdentifier: string;
  identifierType: AuthIdentifiers;
};

export const useCreateOtp = () => {
  const [otpLoading, setOtpLoading] = useState<boolean>(false);
  const [otpError, setOtpError] = useState<boolean>(false);
  const { show } = useToast();
  const { logCreateOtpAttempt } = useCRMEventsContext();
  const { loginJwt } = useGlobalSearchParams<{ loginJwt?: string }>();
  const { deviceId } = useAmplitudeContext();
  /**
   * Get consistent login attempt parameters
   * Stub implementation for device ID for now
   */
  const persistentParameters = useMemo(
    () => ({
      deviceId,
      loginJwt,
    }),
    [loginJwt, deviceId]
  );
  const onCreateOtp = useCallback(data => {
    if (loginJwt) {
      router.replace({
        pathname: '/v2/signin/verify',
        params: { loginJwt: data.createOtpV2.loginJwt },
      });
    } else {
      router.push({
        pathname: '/v2/signin/verify',
        params: { loginJwt: data.createOtpV2.loginJwt },
      });
    }
    return data;
  }, []);

  const onError = useCallback(
    err => {
      if (err?.graphQLErrors && err.graphQLErrors.length > 0) {
        //Take the highest level error code
        const code = err.graphQLErrors[0].extensions.code;
        switch (code) {
          case 'TOO_MANY_REQUESTS':
            show({
              text: 'You have exceeded the limit of attempts. Please try again later.',
              variant: 'informative',
            });
            return;
        }
      }
      show({ text: 'Oops, something went wrong. Please try again', variant: 'negative' });
      setOtpError(true);
    },
    [show]
  );
  const [internalCreateOtp] = useCreateOtpv2Mutation({
    onCompleted: onCreateOtp,
    onError,
  });
  const createOtp = useCallback(
    async (partialInput: CreateOtpInput) => {
      setOtpLoading(true);
      setOtpError(false);
      //Push event to Amplitude
      logCreateOtpAttempt({ identifierType: partialInput.identifierType });
      //Process Create OTP request
      await internalCreateOtp({
        variables: {
          input: {
            ...persistentParameters,
            ...partialInput,
          },
        },
      });
      setOtpLoading(false);
    },
    [internalCreateOtp, persistentParameters, logCreateOtpAttempt]
  );
  return {
    createOtp,
    otpError,
    otpLoading,
  };
};
